import { useState, useEffect, useCallback } from 'react';
import {
  EyeIcon,
  ShieldExclamationIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  type TimeRange,
  getTimeWindow,
  checkAnalyticsHealth,
} from '../services/analyticsService';
import { useAuthStore } from '../store/authStore';
import OverviewTab from '../components/analytics/OverviewTab';
import ThreatTab from '../components/analytics/ThreatTab';
import PatternTab from '../components/analytics/PatternTab';
import InvestigationTab from '../components/analytics/InvestigationTab';

// Phase 8: combined the Situation Awareness + Pattern Analysis tabs into
// a single "Awareness & Patterns" view. Operators were flipping between
// them constantly; the merged tab stacks PatternTab below OverviewTab
// inside one scroll. Threat Analysis + Track Investigation stay separate
// because they're distinct workflows (per-track drilldown vs at-a-glance).
const tabs = [
  { id: 'awareness', label: 'Awareness & Patterns', icon: EyeIcon },
  { id: 'threats', label: 'Threat Analysis', icon: ShieldExclamationIcon },
  { id: 'investigation', label: 'Track Investigation', icon: MagnifyingGlassIcon },
] as const;

type TabId = (typeof tabs)[number]['id'];

const timeRangeLabels: Record<TimeRange, string> = {
  '24h': 'Last 24 Hours',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
};

export default function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('awareness');
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [analyticsAvailable, setAnalyticsAvailable] = useState<boolean | null>(null);

  const tenantId = useAuthStore((s) => s.user?.tenantId) || '';
  const { startTime, endTime } = getTimeWindow(timeRange);

  // Check if analytics service is reachable
  useEffect(() => {
    checkAnalyticsHealth().then(setAnalyticsAvailable);
  }, []);

  // Cross-tab navigation: investigate a track
  const handleInvestigateTrack = useCallback((trackId: string) => {
    setSelectedTrackId(trackId);
    setActiveTab('investigation');
  }, []);

  return (
    <div className="min-h-screen bg-navy-900 p-6 grid-overlay">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="page-title">
            Intelligence Analysis
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {timeRangeLabels[timeRange]} &middot; Drone activity investigation &amp; threat assessment
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button className="btn-secondary flex items-center gap-2">
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export
          </button>
          <div className="flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700">
            {(Object.keys(timeRangeLabels) as TimeRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={clsx(
                  'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                  timeRange === range
                    ? 'bg-accent text-navy-900'
                    : 'text-gray-400 hover:text-white'
                )}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Analytics service warning */}
      {analyticsAvailable === false && (
        <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span>
            Analytics service is unavailable. Showing basic statistics from track data.
            Advanced features (anomaly detection, risk scoring, heatmaps) require the analytics service.
          </span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700 w-fit overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                  activeTab === tab.id
                    ? 'bg-accent text-navy-900'
                    : 'text-gray-400 hover:text-white'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content. Awareness tab stacks the two old views into one
          scroll surface — no duplicate header or range selector since
          both components read range from props passed down here. */}
      {activeTab === 'awareness' && (
        <div className="space-y-6">
          <OverviewTab
            tenantId={tenantId}
            timeRange={timeRange}
            startTime={startTime}
            endTime={endTime}
            analyticsAvailable={analyticsAvailable}
          />
          <PatternTab
            tenantId={tenantId}
            timeRange={timeRange}
            startTime={startTime}
            endTime={endTime}
            analyticsAvailable={analyticsAvailable}
            onInvestigateTrack={handleInvestigateTrack}
          />
        </div>
      )}
      {activeTab === 'threats' && (
        <ThreatTab
          tenantId={tenantId}
          timeRange={timeRange}
          startTime={startTime}
          endTime={endTime}
          analyticsAvailable={analyticsAvailable}
          onInvestigateTrack={handleInvestigateTrack}
        />
      )}
      {activeTab === 'investigation' && (
        <InvestigationTab
          tenantId={tenantId}
          timeRange={timeRange}
          startTime={startTime}
          endTime={endTime}
          analyticsAvailable={analyticsAvailable}
          selectedTrackId={selectedTrackId}
          onSelectTrack={setSelectedTrackId}
        />
      )}
    </div>
  );
}
